import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import { ICONS } from '@shared/components/icons/icon.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSearchParams } from 'react-router-dom';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import ViewControls from '@components/view-controls/view-controls.tsx';
import { getProductCategories } from '@api/product-categories-api.ts';
import useProductCategoriesStore from '@modules/product-categories/store/product-categories.store.ts';
import { ProductCategoriesModel } from '@models/product-categories.model.ts';
import ProductCategoriesDialog from '@modules/product-categories/components/product-categories-dialog/product-categories-dialog.tsx';
import { productCategoriesColumns } from '@modules/product-categories/components/product-categories-table/components/columns/product-categories-columns.tsx';
import { TriggerTypeEnum } from '@shared/models/table.model';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';

const ProductCategoriesTable: React.FC = () => {
  const {
    createProductCategories,
    editProductCategories,
    deleteProductCategories,
    departmentUnit,
    getDepartmentUnit,
    isLoading
  } = useProductCategoriesStore();

  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [updateProductCategories, setUpdateProductCategories] =
    useState<ProductCategoriesModel | null>(null);
  const [language, setLanguage] = useState(LanguageEnum.English);
  const [searchParams, setSearchParams] = useSearchParams();
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');

  useEffect(() => {
    const fetchDataAndOpenDialog = async () => {
      await Promise.all([getDepartmentUnit()]);

      const dialog = searchParams.get('dialog');
      if (dialog === 'open') {
        handleOpen();
      }
    };

    fetchDataAndOpenDialog().then();
  }, []);

  const handleEdit = (row: ProductCategoriesModel) => {
    setUpdateProductCategories(row);
    setOpenDialog(true);
  };

  const handleDelete = (row: ProductCategoriesModel) => {
    setUpdateProductCategories(row);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    deleteProductCategories({
      id: updateProductCategories?.id,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleClose = () => {
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setUpdateProductCategories(null);
  };

  const handleOpen = () => setOpenDialog(true);

  const handleFormSave = (data: any) => {
    if (updateProductCategories?.id) {
      editProductCategories({
        data,
        id: updateProductCategories?.id,
        state: tableStore.getState(),
        handleClose
      });
    } else {
      createProductCategories({
        data,
        state: tableStore.getState(),
        handleClose
      });
    }
  };

  const handleAction = (action: { type: string; value?: string }) => {
    if (action.type === ViewControlsEnum.Language) {
      setLanguage(action.value as LanguageEnum);
    }
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  const columns = productCategoriesColumns(
    handleEdit,
    handleDelete,
    language,
    departmentUnit
  );

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls onAction={handleAction} />

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={handleOpen}
        >
          {t('add')}
        </Button>
      </div>

      <div className={TABLE_HEIGHT.WITH_TABS_AND_ACTIONS}>
        <DynamicTable
          tableStore={tableStore}
          apiFunction={getProductCategories}
          onTriggerAction={handleTableTrigger}
          columns={columns}
          language={language}
          tableKey="product-categories-table"
        />
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          size={isTabletScreen ? 'md' : 'lg'}
          title={t('product_categories_dialog_title')}
          content={
            <ProductCategoriesDialog
              updateProductCategoriesData={updateProductCategories}
              onSave={handleFormSave}
            />
          }
          onClose={handleClose}
        />

        <div className="w-full h-full">
          <DeleteDialog
            onCancel={handleClose}
            onDelete={handleDeleteConfirm}
            open={showDeleteDialog}
            isLoading={isLoading()}
            title={t('delete_popup_title')}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCategoriesTable;
